import React, { useState, useEffect } from 'react';
import { getClientById, updateClient } from '../api';
import { FiX } from 'react-icons/fi';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from './InlineLoading';
import { notify } from '../utils/alertBus';

const EditClientModal = ({ clientId, onClose, onUpdated }) => {
  const [client, setClient] = useState({
    name: '',
    email: '',
    giro: '',
    rut: '',
    address: '',
    city: '',
    phone: '',
    creditLimit: '',
    creditBlocked: false,
    creditWarningNote: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useCurrentUser();
  const canManageCredit = hasPermission(user, 'branch.access.any');

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const data = await getClientById(clientId);
        setClient(data);
      } catch (err) {
        setError('Error al cargar los datos del cliente.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchClient();
  }, [clientId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClient((prevClient) => ({ ...prevClient, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...client };
      if (canManageCredit) {
        const trimmedLimit = String(client.creditLimit ?? '').trim();
        if (trimmedLimit === '') {
          payload.creditLimit = null;
        } else {
          const parsedLimit = Number(trimmedLimit);
          if (!Number.isFinite(parsedLimit) || parsedLimit < 0) {
            setError('El limite de credito debe ser un numero valido.');
            return;
          }
          payload.creditLimit = parsedLimit;
        }
        payload.creditBlocked = Boolean(client.creditBlocked);
        payload.creditWarningNote = client.creditWarningNote?.trim() || null;
      } else {
        delete payload.creditLimit;
        delete payload.creditBlocked;
        delete payload.creditWarningNote;
      }
      await updateClient(clientId, payload);
      notify('Cliente actualizado exitosamente.');
      if (onUpdated) onUpdated();
      onClose();
    } catch (err) {
      setError('Error al actualizar el cliente.');
      console.error(err);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Clientes</p>
            <h1 className="text-lg font-semibold text-gray-900">Editar cliente</h1>
            <p className="text-xs text-gray-500 mt-1">Actualiza datos y condiciones de crédito.</p>
          </div>
          <button className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-6">
            <p className="text-center">
              <InlineLoading />
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
                <input type="text" name="name" value={client.name} onChange={handleChange} placeholder="Nombre" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" required />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Email</label>
                <input type="email" name="email" value={client.email} onChange={handleChange} placeholder="Email" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">RUT</label>
                <input type="text" name="rut" value={client.rut} onChange={handleChange} placeholder="RUT" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" required />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Giro</label>
                <input type="text" name="giro" value={client.giro || ''} onChange={handleChange} placeholder="Giro" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Teléfono</label>
                <input type="text" name="phone" value={client.phone} onChange={handleChange} placeholder="Teléfono" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-600 uppercase">Dirección</label>
                <input type="text" name="address" value={client.address} onChange={handleChange} placeholder="Dirección" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-gray-600 uppercase">Ciudad</label>
                <input type="text" name="city" value={client.city} onChange={handleChange} placeholder="Ciudad" className="mt-2 w-full p-3 border border-gray-300 rounded-lg" />
              </div>
            </div>

            {canManageCredit && (
              <div className="border-t border-gray-100 pt-4 space-y-3">
                <h2 className="text-sm font-semibold text-gray-700">Credito</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase">Límite de crédito</label>
                    <input
                      type="number"
                      min="0"
                      name="creditLimit"
                      value={client.creditLimit ?? ''}
                      onChange={handleChange}
                      placeholder="Limite de credito (opcional)"
                      className="mt-2 w-full p-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full">
                      <input
                        type="checkbox"
                        name="creditBlocked"
                        checked={Boolean(client.creditBlocked)}
                        onChange={handleChange}
                      />
                      Bloquear credito para este cliente
                    </label>
                  </div>
                </div>
                <textarea
                  name="creditWarningNote"
                  value={client.creditWarningNote || ''}
                  onChange={handleChange}
                  placeholder="Nota de advertencia"
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
            )}
            <div className="flex items-center justify-between border-t border-gray-100 pt-4">
              <div className="text-xs text-gray-500">
                Los cambios se aplican al guardar.
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:text-gray-900">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Guardar</button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditClientModal;
